// [phase12cf-signal-icon-v1] Canonical Signal icon — Trading UI.
//
// Broadcast/radar glyph: centered dot with symmetric arcs on both sides.
// Uses `currentColor` so the icon inherits its parent text color — works
// on the dark theme automatically and matches the Lucide icon convention.
//
// Call sites pass `className` like a Lucide component (e.g.
// `<SignalIcon className="h-5 w-5" />`), or `size`/`color` props directly.
//
// Accessible labeling:
//   * Decorative (default): no role/label; parent provides accessible text.
//   * Non-decorative: pass `aria-label="The Signal"` and `role="img"`.
//
// Identity-preserving — do not alter viewBox or path geometry.

import * as React from "react";

export interface SignalIconProps extends React.SVGAttributes<SVGSVGElement> {
  size?: number | string;
  color?: string;
  strokeWidth?: number;
  /** When true, the icon is rendered as an accessible image labelled "The Signal". */
  labelled?: boolean;
}

const SignalIcon = React.forwardRef<SVGSVGElement, SignalIconProps>(
  function SignalIcon(
    {
      size,
      color,
      strokeWidth,
      labelled,
      className,
      style,
      "aria-label": ariaLabel,
      ...rest
    },
    ref,
  ) {
    const accessible = labelled || Boolean(ariaLabel);
    const stroke = (strokeWidth ?? 13) as number;
    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 256 256"
        width={size}
        height={size}
        className={className}
        style={style}
        // Decorative by default; explicit aria-label flips to img.
        role={accessible ? "img" : undefined}
        aria-label={accessible ? ariaLabel || "The Signal" : undefined}
        aria-hidden={accessible ? undefined : true}
        fill={color ?? "none"}
        stroke={color ?? "currentColor"}
        strokeLinecap="round"
        strokeLinejoin="round"
        data-signal-icon="phase12cf-signal-icon-v1"
        {...rest}
      >
        {accessible ? <title>The Signal</title> : null}
        <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          {/* Left-side arcs (radio waves coming from the centered transmitter). */}
          <path d="M54 52C30 75 18 102 18 128s12 53 36 76" strokeWidth={stroke} />
          <path d="M82 78c-16 15-24 32-24 50s8 35 24 50"      strokeWidth={stroke} />
          <path d="M108 104c-8 7-12 15-12 24s4 17 12 24"      strokeWidth={stroke} />
          {/* Right-side arcs. */}
          <path d="M202 52c24 23 36 50 36 76s-12 53-36 76" strokeWidth={stroke} />
          <path d="M174 78c16 15 24 32 24 50s-8 35-24 50"   strokeWidth={stroke} />
          <path d="M148 104c8 7 12 15 12 24s-4 17-12 24"    strokeWidth={stroke} />
        </g>
        {/* Center dot — the canonical centered transmitter. */}
        <circle cx="128" cy="128" r="15" fill="currentColor" />
      </svg>
    );
  },
);

export default SignalIcon;
